/**
 * PATH: packages/email-hub/src/feature/stores/composePendingStore.ts
 * FILE_ID: EMAILHUB-FEATURE-STORES-COMPOSEPENDINGSTORE-001
 * PURPOSE: Canonical body of this email stores module. Lifted
 *          from apps/<tenant>/src/features/email/stores/composePendingStore.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/stores/composePendingStore.ts: thin re-export
 *     shim that points back here.
 */

import { create } from 'zustand'

import { emailPath } from '../lib/basePath'

export type ComposeIntent =
  | { kind: 'new' }
  | { kind: 'reply'; emailId: string }
  | { kind: 'forward'; emailId: string }

export interface PendingCompose {
  /** Files to pre-populate ComposePage's `attachments: File[]`. */
  files: File[]
  intent: ComposeIntent
  /** Optional pre-filled subject. Useful for "Marked-up screenshot
   *  – 9 May 2026 19:34" so the operator doesn't have to retype. */
  subject?: string
  /** Optional pre-filled body text appended *before* any reply
   *  quote that the existing reply pre-fill builds. */
  bodyPrefix?: string
  /** Optional image (as a ``data:`` URL) to embed INLINE at the top of
   *  the compose body — used by the markup drawer so a marked-up image
   *  shows up *in the email body* (above the reply quote), not just as a
   *  detached attachment the recipient has to open. Operator (2026-05-31):
   *  "send as a reply doesn't have an in-mail of the markup". */
  inlineImageDataUrl?: string
}

interface PendingState {
  pending: PendingCompose | null
  setPending: (next: PendingCompose) => void
  /** Read-and-clear in one step. Returns ``null`` if nothing is
   *  queued, otherwise returns the value and empties the slot. */
  consume: () => PendingCompose | null
  clear: () => void
}

export const useComposePendingStore = create<PendingState>((set, get) => ({
  pending: null,
  setPending: (next) => set({ pending: next }),
  consume: () => {
    const current = get().pending
    if (current) set({ pending: null })
    return current
  },
  clear: () => set({ pending: null }),
}))

/** Convenience helper: build a relative compose-page URL from an
 *  intent. Centralised here so callers don't have to re-derive
 *  `?reply=` vs `?forward=` and risk drifting from ComposePage's
 *  search-param contract. */
export function composeUrlFor(intent: ComposeIntent): string {
  // Prefix with the host's configured mount path (emailPath) — the
  // master Bravada shell mounts the hub at /emails, SAM at /email, the
  // standalone hub at /. Hard-coding /email here sent master users to a
  // non-existent route, which the router bounced to the dashboard.
  const base = emailPath('/compose')
  if (intent.kind === 'reply') return `${base}?reply=${encodeURIComponent(intent.emailId)}`
  if (intent.kind === 'forward') return `${base}?forward=${encodeURIComponent(intent.emailId)}`
  return base
}

/** Build the pending-compose payload for the "Use this draft" action
 *  on a quote-request AI draft reply. The draft body is stashed as
 *  `bodyPrefix` (not `subject` body) so ComposePage prepends it ABOVE
 *  the auto-built reply quote — same hand-off the markup-image flow
 *  uses. Until this wiring, `onUseDraftReply` had no consumer and the
 *  draft only ever reached the clipboard. Pure I->O so the EmailReader
 *  wiring is unit-testable without mounting the reader.
 *  An empty/blank subject collapses to `undefined` so ComposePage's
 *  reply prefill can build the canonical `Re: …` instead. */
export function buildDraftReplyPending(
  emailId: string,
  draft: { subject?: string; body: string },
): PendingCompose {
  const subject = (draft.subject || '').trim()
  return {
    files: [],
    intent: { kind: 'reply', emailId },
    subject: subject || undefined,
    bodyPrefix: draft.body,
  }
}
